import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { type Group, useDeleteGroup } from "@/lib/api";
import { logger } from "@/lib/logger";

interface DeleteGroupDialogProps {
  group: Group | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteGroupDialog({ group, open, onOpenChange }: DeleteGroupDialogProps) {
  const deleteGroup = useDeleteGroup();
  const [error, setError] = useState("");
  const deleting = deleteGroup.isPending;

  const handleDelete = async () => {
    if (!group) {
      return;
    }
    setError("");

    try {
      await deleteGroup.mutateAsync({ groupId: group.id });
      onOpenChange(false);
    } catch (err) {
      logger.error("[delete-group] failed:", err);
      setError("Failed to delete group. Please try again.");
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Group</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete <strong>{group?.name}</strong>? Its member projects
            aren&apos;t deleted &mdash; they become ungrouped, visible only to their owner and
            anyone added as a member. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && <p className="text-destructive text-sm">{error}</p>}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete Group"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
